import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import Button from '../../shared/UI/Button';
// The formula catalogue lives in src/config/formulaTypes.js so the dropdown,
// the derived-result badge and the analytics labels all name a formula the
// same way. Add a formula there, not here.
import { FORMULA_GROUPS } from '../../../config/formulaTypes';

// Sentinel option value for the GENERAL formula's combined field picker, which
// lists every question plus the PIF requested amount in one dropdown. Cannot
// collide with a question_id (those are generated as 'q_...').
const PIF_REQUESTED_AMOUNT = '__pif_requested_amount__';

// For each formula: what inputs does the configurator need to collect?
// Each entry is: { param, label, type } where type is 'question' | 'pif_field' | 'multi_question'
// GENERAL additionally uses: 'field' (question or PIF field in one dropdown),
// 'constant' (optional numeric coefficient) and 'operator' (stored in
// general_config, not formula_inputs — see the renderer below).
const FORMULA_INPUTS = {
  GROWTH_RATE: [
    { param: 'current', label: 'Current Period Question', type: 'question' },
    { param: 'previous', label: 'Previous Period Question', type: 'question' },
  ],
  AVERAGE: [{ param: 'values', label: 'Questions to Average', type: 'multi_question' }],
  EBITDA_MARGIN: [
    { param: 'ebitda', label: 'EBITDA Question', type: 'question' },
    { param: 'revenue', label: 'Revenue Question', type: 'question' },
  ],
  NET_MARGIN: [
    { param: 'net_profit', label: 'Net Profit Question', type: 'question' },
    { param: 'revenue', label: 'Revenue Question', type: 'question' },
  ],
  DSCR: [
    { param: 'ebitda', label: 'EBITDA Question', type: 'question' },
    { param: 'annual_debt_service', label: 'Annual Debt Service Question', type: 'question' },
  ],
  CURRENT_RATIO: [
    { param: 'current_assets', label: 'Current Assets Question', type: 'question' },
    { param: 'current_liabilities', label: 'Current Liabilities Question', type: 'question' },
  ],
  ASSET_COVERAGE_RATIO: [
    { param: 'net_assets', label: 'Net Assets Question', type: 'question' },
    { param: 'total_debt', label: 'Total Debt Question', type: 'question' },
  ],
  LEVERAGE: [
    { param: 'total_debt', label: 'Total Debt Question', type: 'question' },
    { param: 'ebitda', label: 'EBITDA Question', type: 'question' },
  ],
  DEBT_TO_EQUITY: [
    { param: 'total_debt', label: 'Total Debt Question', type: 'question' },
    { param: 'total_equity', label: 'Total Equity Question', type: 'question' },
  ],
  REVENUE_PER_EMPLOYEE: [
    { param: 'revenue', label: 'Revenue Question', type: 'question' },
    { param: 'total_employees', label: 'Total Employees Question', type: 'question' },
  ],
  CAPITAL_EFFICIENCY: [
    { param: 'revenue', label: 'Revenue Question', type: 'question' },
    { param: 'total_assets', label: 'Total Assets Question', type: 'question' },
  ],
  REVENUE_MULTIPLE: [
    { param: 'revenue', label: 'Revenue Question', type: 'question' },
    { param: 'investment_amount', label: 'Investment Amount', type: 'pif_field', field: 'requested_amount' },
  ],
  EBITDA_MULTIPLE: [
    { param: 'ebitda', label: 'EBITDA Question', type: 'question' },
    { param: 'investment_amount', label: 'Investment Amount', type: 'pif_field', field: 'requested_amount' },
  ],
  RATIO: [
    { param: 'numerator', label: 'Numerator Question', type: 'question' },
    { param: 'denominator', label: 'Denominator Question', type: 'question' },
  ],
  PERCENTAGE: [
    { param: 'part', label: 'Part Question', type: 'question' },
    { param: 'whole', label: 'Whole Question', type: 'question' },
  ],
  SUM: [{ param: 'values', label: 'Questions to Sum', type: 'multi_question' }],
  FEMALE_RATIO: [
    { param: 'female_employees', label: 'Female Employees Question', type: 'question' },
    { param: 'total_employees', label: 'Total Employees Question', type: 'question' },
  ],
  FEMALE_LEADERSHIP_RATIO: [
    { param: 'female_leaders', label: 'Female Leaders Question', type: 'question' },
    { param: 'total_leaders', label: 'Total Leaders Question', type: 'question' },
  ],
  BENEFICIARY_RATIO: [
    { param: 'beneficiaries', label: 'Beneficiaries Question', type: 'question' },
    { param: 'total_population', label: 'Total Population Question', type: 'question' },
  ],
  IMPACT_REACH_RATIO: [
    { param: 'beneficiaries', label: 'Beneficiaries Question', type: 'question' },
    { param: 'investment_amount', label: 'Investment Amount', type: 'pif_field', field: 'requested_amount' },
  ],
  // Field B is optional: leave it blank for a single-term formula such as
  // 7 x Revenue. The operator and coefficient b are only rendered once a
  // second field is chosen, so Field B is ordered ahead of them.
  GENERAL: [
    { param: 'coeff_a', label: 'Coefficient a (optional — blank means 1)', type: 'constant' },
    { param: 'field_a', label: 'Field A', type: 'field' },
    { param: 'field_b', label: 'Field B (optional — blank means a single-term formula)', type: 'field' },
    { param: 'operator', label: 'Operator', type: 'operator' },
    { param: 'coeff_b', label: 'Coefficient b (optional — blank means 1)', type: 'constant' },
  ],
};

// Human-readable preview text for each formula
function buildFormulaPreview(q, nonDerivedQuestions) {
  if (!q.formula_type) return '';
  const qLabel = (qid) => {
    const found = nonDerivedQuestions.find(x => x.question_id === qid);
    return found ? `"${found.question_text}"` : '[not selected]';
  };
  const inp = q.formula_inputs || {};
  const get = (param) => {
    const ref = inp[param];
    if (!ref) return '[not selected]';
    if (ref.type === 'question') return qLabel(ref.question_id);
    if (ref.type === 'pif_field') return 'investment amount (from PIF form)';
    return '[not selected]';
  };

  if (isCagrType(q.formula_type)) {
    const cfg = q.cagr_config || {};
    const ids = cfg.year_question_ids || [];
    const first = ids[0] ? qLabel(ids[0]) : '[Year 1]';
    const last = ids[ids.length - 1] ? qLabel(ids[ids.length - 1]) : '[Year N]';
    const inUsd = q.formula_type === 'CAGR_USD'
      ? ', converting each PAST year to USD at that year\'s actual year-end rate'
      : q.formula_type === 'CAGR_USD_FUTURE'
        ? ', converting each PROJECTED year to USD at a projected year-end rate ' +
          '(the base rate depreciated per year)'
        : '';
    const forward = q.formula_type === 'CAGR_USD_FUTURE'
      ? ' forward from the current financial year'
      : '';
    return `Will calculate CAGR${forward} from ${first} to ${last} over up to ${cfg.max_periods || '?'} years (minimum ${cfg.min_periods || '?'})${inUsd}`;
  }
  if (q.formula_type === 'DSCR') {
    return `Will calculate DSCR as ${get('ebitda')} ÷ ${get('annual_debt_service')}`;
  }
  if (q.formula_type === 'RATIO') {
    return `Will calculate ${get('numerator')} as a ratio of ${get('denominator')}`;
  }
  if (q.formula_type === 'PERCENTAGE') {
    return `Will calculate ${get('part')} as a percentage of ${get('whole')}`;
  }
  if (q.formula_type === 'EBITDA_MARGIN') {
    return `Will calculate EBITDA Margin as ${get('ebitda')} ÷ ${get('revenue')}`;
  }
  if (q.formula_type === 'NET_MARGIN') {
    return `Will calculate Net Margin as ${get('net_profit')} ÷ ${get('revenue')}`;
  }
  if (q.formula_type === 'LEVERAGE') {
    return `Will calculate Debt Multiple as ${get('total_debt')} ÷ ${get('ebitda')}`;
  }
  if (q.formula_type === 'GROWTH_RATE') {
    return `Will calculate Growth Rate as (${get('current')} − ${get('previous')}) ÷ ${get('previous')}`;
  }
  if (q.formula_type === 'REVENUE_MULTIPLE') {
    return `Will calculate Revenue Multiple as ${get('investment_amount')} ÷ ${get('revenue')}`;
  }
  if (q.formula_type === 'EBITDA_MULTIPLE') {
    return `Will calculate EBITDA Multiple as ${get('investment_amount')} ÷ ${get('ebitda')}`;
  }
  if (q.formula_type === 'GENERAL') {
    const coeff = (param) => {
      const ref = inp[param];
      if (!ref || ref.value === '' || ref.value == null) return '';
      return `${ref.value} × `;
    };
    // No Field B — single term, e.g. "7 × Revenue"
    if (!inp.field_b) {
      return `Will calculate ${coeff('coeff_a')}${get('field_a')}`;
    }
    const opLabel = { '+': '+', '-': '−', '*': '×', '/': '÷' }[q.general_config?.operator];
    return `Will calculate (${coeff('coeff_a')}${get('field_a')}) ` +
      `${opLabel || '[operator]'} (${coeff('coeff_b')}${get('field_b')})`;
  }
  if (q.formula_type === 'SUM' || q.formula_type === 'AVERAGE') {
    const refs = inp.values || [];
    const labels = refs.map(r => qLabel(r.question_id)).join(', ');
    return `Will calculate ${q.formula_type === 'SUM' ? 'sum' : 'average'} of: ${labels || '[no questions selected]'}`;
  }
  return `Will compute ${q.formula_type}`;
}

function makeQId() {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 6);
  return 'q_' + ts + '_' + rand;
}

function makeOptId(qId, idx) {
  return `${qId}_${String.fromCharCode(97 + idx)}`;
}

// Fix 2 Stage A — currencies a rubric's monetary score bands can be defined in
const MONETARY_CURRENCIES = [
  'USD', 'GHS', 'NGN', 'EUR', 'GBP', 'XOF',
  'KES', 'ZAR', 'EGP', 'ETB', 'TZS', 'UGX',
];

// Formula types that take their inputs from cagr_config.year_question_ids
// rather than formula_inputs, and so share the CAGR configuration panel.
// CAGR is the reporting-currency growth rate; CAGR_USD is PAST USD growth at
// actual year-end rates; CAGR_USD_FUTURE is FORWARD USD growth over projected
// revenues at projected rates. Same configuration shape, different metric —
// see functions/formulaLibrary.js.
const CAGR_TYPES = ['CAGR', 'CAGR_USD', 'CAGR_USD_FUTURE'];
const isCagrType = t => CAGR_TYPES.includes(t);

// Advisory check for the optional fiscal_year on figure questions. Blank/unset is
// always acceptable, so it returns true — the caller only shows a hint when this is
// false. This never feeds the errors object: saving and publishing are never blocked
// by fiscal_year. Real enforcement belongs at the point of consumption, where an
// unusable year can be refused with a specific reason.
function isPlausibleFiscalYear(value) {
  if (value == null || value === '') return true;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1900 && n <= 2200;
}

function defaultQuestion(order) {
  const qid = makeQId();
  return {
    question_id: qid,
    order,
    question_text: '',
    response_type: 'binary',
    weight: 1,
    is_required: true,
    allow_document_upload: false,
    options: [
      { option_id: `${qid}_a`, option_text: 'Yes', score: 1 },
      { option_id: `${qid}_b`, option_text: 'No', score: 0 },
    ],
    figure_unit: '',
    figure_score_bands: [],
    // ASSUMPTION: all fiscal years in this app end in DECEMBER, so fiscal_year
    // is a plain 4-digit CALENDAR year (e.g. 2023) rather than an FY label with
    // a year-end month. If off-calendar year-ends are ever supported, this
    // assumption must be revisited here and everywhere fiscal_year is consumed.
    // Optional everywhere: null means "not stated". Nothing reads it yet.
    fiscal_year: null,
    // Fix 2 Stage A — monetary flag (data capture only, no scoring change yet)
    is_monetary: false,
    rubric_currency: null,
    // Derived question fields (populated when response_type === 'derived')
    is_derived: false,
    show_to_applicant: true,
    formula_type: '',
    formula_inputs: {},
    cagr_config: null,
    display_format: 'decimal',
    display_decimal_places: 2,
    // Date question fields
    date_scoring_method: 'binary',
    date_score_bands: [],
    binary_date_score: 10,
    // Text question fields
    text_score: 10,
  };
}

export default function QuestionBuilder({ questions = [], onChange, errors = {}, showScores = true }) {
  // Non-derived questions in this rubric — used to populate formula input selectors
  const nonDerivedQuestions = questions.filter(q => !q.is_derived);
  function add() {
    onChange([...questions, defaultQuestion(questions.length + 1)]);
  }

  function remove(i) {
    onChange(questions.filter((_, idx) => idx !== i).map((q, idx) => ({ ...q, order: idx + 1 })));
  }

  function moveUp(i) {
    if (i === 0) return;
    const arr = [...questions];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    onChange(arr.map((q, idx) => ({ ...q, order: idx + 1 })));
  }

  function moveDown(i) {
    if (i === questions.length - 1) return;
    const arr = [...questions];
    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
    onChange(arr.map((q, idx) => ({ ...q, order: idx + 1 })));
  }

  function setQ(i, field, value) {
    onChange(questions.map((q, idx) => {
      if (idx !== i) return q;
      const next = { ...q, [field]: value };
      if (field === 'response_type') {
        // NOTE: fiscal_year is deliberately NOT cleared on a type switch, unlike
        // figure_unit / figure_score_bands below. It is admin-entered data that a
        // later step depends on, the input only renders for figure questions, and
        // nothing reads the field off a non-figure question — so keeping it costs
        // nothing and an accidental toggle does not destroy it.
        if (value === 'binary') {
          next.options = [
            { option_id: `${q.question_id}_a`, option_text: 'Yes', score: 1 },
            { option_id: `${q.question_id}_b`, option_text: 'No', score: 0 },
          ];
          next.figure_unit = '';
          next.figure_score_bands = [];
          next.is_derived = false;
          next.show_to_applicant = true;
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = null;
        } else if (value === 'multiple_choice') {
          next.options = [
            { option_id: makeOptId(q.question_id, 0), option_text: '', score: 0 },
            { option_id: makeOptId(q.question_id, 1), option_text: '', score: 0 },
          ];
          next.figure_unit = '';
          next.figure_score_bands = [];
          next.is_derived = false;
          next.show_to_applicant = true;
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = null;
        } else if (value === 'derived') {
          next.options = [];
          next.figure_unit = '';
          next.figure_score_bands = [{ min: 0, max: 0, score: 0 }];
          next.is_derived = true;
          next.show_to_applicant = false; // always false — spec rule 1
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = { max_periods: 5, min_periods: 2, year_question_ids: [] };
          next.display_format = 'decimal';
          next.display_decimal_places = 2;
        } else if (value === 'date') {
          next.options = [];
          next.figure_unit = '';
          next.figure_score_bands = [];
          next.is_derived = false;
          next.show_to_applicant = true;
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = null;
          next.date_scoring_method = 'binary';
          next.date_score_bands = [
            { max_years: 1, score: 10 },
            { max_years: 2, score: 6 },
            { max_years: 5, score: 2 },
            { max_years: null, score: 0 },
          ];
          next.binary_date_score = 10;
        } else if (value === 'text' || value === 'long_text') {
          next.options = [];
          next.figure_unit = '';
          next.figure_score_bands = [];
          next.is_derived = false;
          next.show_to_applicant = true;
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = null;
          next.text_score = 10;
        } else {
          // figure
          next.options = [];
          next.figure_score_bands = [{ min: 0, max: 0, score: 0 }];
          next.is_derived = false;
          next.show_to_applicant = true;
          next.formula_type = '';
          next.formula_inputs = {};
          next.cagr_config = null;
        }
      }
      return next;
    }));
  }

  function setOpt(qi, oi, field, value) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return {
        ...q,
        options: q.options.map((o, oidx) =>
          oidx === oi ? { ...o, [field]: field === 'score' ? (parseFloat(value) || 0) : value } : o
        ),
      };
    }));
  }

  function addOpt(qi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi || q.options.length >= 25) return q;
      return {
        ...q,
        options: [...q.options, {
          option_id: makeOptId(q.question_id, q.options.length),
          option_text: '',
          score: 0,
        }],
      };
    }));
  }

  function removeOpt(qi, oi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi || q.options.length <= 2) return q;
      return { ...q, options: q.options.filter((_, oidx) => oidx !== oi) };
    }));
  }

  function setBand(qi, bi, field, value) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return {
        ...q,
        figure_score_bands: q.figure_score_bands.map((b, bidx) =>
          bidx === bi ? { ...b, [field]: parseFloat(value) || 0 } : b
        ),
      };
    }));
  }

  function addBand(qi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return { ...q, figure_score_bands: [...(q.figure_score_bands || []), { min: 0, max: 0, score: 0 }] };
    }));
  }

  function removeBand(qi, bi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return { ...q, figure_score_bands: q.figure_score_bands.filter((_, bidx) => bidx !== bi) };
    }));
  }

  function setDateBand(qi, bi, field, value) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return {
        ...q,
        date_score_bands: (q.date_score_bands || []).map((b, bidx) =>
          bidx === bi ? {
            ...b,
            [field]: field === 'max_years'
              ? (value === '' || value === null ? null : parseInt(value))
              : (parseFloat(value) || 0),
          } : b
        ),
      };
    }));
  }

  function addDateBand(qi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return { ...q, date_score_bands: [...(q.date_score_bands || []), { max_years: null, score: 0 }] };
    }));
  }

  function removeDateBand(qi, bi) {
    onChange(questions.map((q, idx) => {
      if (idx !== qi) return q;
      return { ...q, date_score_bands: (q.date_score_bands || []).filter((_, bidx) => bidx !== bi) };
    }));
  }

  return (
    <div>
      {questions.map((q, i) => (
        <div key={q.question_id} className="question-card">
          <div className="question-card-header">
            <div>
              <div>
                Question {q.order}
                {q.is_derived && (
                  <span style={{
                    marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11,
                    fontWeight: 700, background: 'rgba(201,149,42,0.15)',
                    color: '#c9952a', letterSpacing: '0.03em',
                  }}>DERIVED</span>
                )}
                {q.is_monetary && (
                  <span style={{
                    marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11,
                    fontWeight: 700, background: 'var(--color-border-light)',
                    color: 'var(--color-text-secondary)', letterSpacing: '0.03em',
                  }}>{q.rubric_currency || 'USD'}</span>
                )}
              </div>
              <div style={{ marginTop: 3 }}>
                {q.section ? (
                  <span style={{
                    display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                    fontSize: 11, background: 'var(--color-border-light)',
                    color: 'var(--color-text-secondary)',
                  }}>{q.section}</span>
                ) : (
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                    Untagged
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Button size="sm" variant="ghost" onClick={() => moveUp(i)} disabled={i === 0}>↑</Button>
              <Button size="sm" variant="ghost" onClick={() => moveDown(i)} disabled={i === questions.length - 1}>↓</Button>
              <Button size="sm" variant="danger" onClick={() => remove(i)}>Remove</Button>
            </div>
          </div>
          <div className="question-card-body">
            <div className="form-grid">
              <FormField label="Question Text" required error={errors[`q_${i}_text`]} className="form-full">
                <textarea
                  className={`form-textarea ${errors[`q_${i}_text`] ? 'error' : ''}`}
                  value={q.question_text}
                  onChange={e => setQ(i, 'question_text', e.target.value)}
                  rows={2}
                  placeholder="Enter the question as shown to the company…"
                />
              </FormField>
              <FormField label="Section (optional)" className="form-full">
                <input
                  list={`section-options-${q.question_id}`}
                  className="form-input"
                  value={q.section || ''}
                  onChange={e => setQ(i, 'section', e.target.value)}
                  placeholder="Select or type a section name"
                />
                <datalist id={`section-options-${q.question_id}`}>
                  <option value="Basic Company Information" />
                  <option value="Investment Needs and Growth Plans" />
                  <option value="Financial Performance" />
                  <option value="Legal & Governance" />
                  <option value="Operations" />
                  <option value="Jobs & Gender" />
                  <option value="Market & Strategy" />
                  <option value="Impact" />
                  <option value="Management" />
                  <option value="Other" />
                </datalist>
              </FormField>
              <SelectField
                label="Response Type"
                value={q.response_type}
                onChange={e => setQ(i, 'response_type', e.target.value)}
                options={[
                  { value: 'binary', label: 'Binary (Yes / No)' },
                  { value: 'multiple_choice', label: 'Multiple Choice' },
                  { value: 'figure', label: 'Figure (Numeric)' },
                  { value: 'date', label: 'Date (calendar picker)' },
                  { value: 'text', label: 'Text (single line)' },
                  { value: 'long_text', label: 'Long Text (paragraph)' },
                  { value: 'derived', label: 'Derived (Computed Formula)' },
                ]}
              />
              {showScores && (
                <FormField label="Weight" required error={errors[`q_${i}_weight`]}>
                  <input
                    type="number" min="0" step="0.1"
                    className={`form-input ${errors[`q_${i}_weight`] ? 'error' : ''}`}
                    value={q.weight}
                    onChange={e => setQ(i, 'weight', parseFloat(e.target.value) || 0)}
                  />
                </FormField>
              )}
              <ToggleField label="Is Required" checked={q.is_required} onChange={v => setQ(i, 'is_required', v)} />
              <ToggleField label="Allow Document Upload" checked={q.allow_document_upload} onChange={v => setQ(i, 'allow_document_upload', v)} />
            </div>

            {/* Binary / Multiple Choice options */}
            {(q.response_type === 'binary' || q.response_type === 'multiple_choice') && (
              <div className="mt-3">
                <p className="form-label mb-2">Response Options</p>
                {q.options.map((opt, oi) => (
                  <div key={opt.option_id} className="option-row">
                    <input
                      className="form-input"
                      value={opt.option_text}
                      onChange={e => setOpt(i, oi, 'option_text', e.target.value)}
                      placeholder="Option text"
                      readOnly={q.response_type === 'binary'}
                      style={q.response_type === 'binary' ? { background: 'var(--color-border-light)', cursor: 'not-allowed' } : {}}
                    />
                    {showScores && (
                      <input
                        type="number" step="0.1"
                        className="form-input"
                        style={{ width: 100 }}
                        value={opt.score}
                        onChange={e => setOpt(i, oi, 'score', e.target.value)}
                        placeholder="Score"
                      />
                    )}
                    {q.response_type === 'multiple_choice' && q.options.length > 2 && (
                      <Button size="sm" variant="ghost" onClick={() => removeOpt(i, oi)}>×</Button>
                    )}
                  </div>
                ))}
                {q.response_type === 'multiple_choice' && q.options.length < 25 && (
                  <Button size="sm" variant="secondary" onClick={() => addOpt(i)} className="mt-2">+ Add Option</Button>
                )}
              </div>
            )}

            {/* Figure type */}
            {q.response_type === 'figure' && (
              <div className="mt-3">
                <div style={{ maxWidth: 260, marginBottom: 12 }}>
                  <FormField label="Figure Unit" hint="e.g. USD, employees, years">
                    <input className="form-input" value={q.figure_unit || ''} placeholder="e.g. USD"
                      onChange={e => setQ(i, 'figure_unit', e.target.value)} />
                  </FormField>
                </div>
                {/* Optional calendar year this figure relates to. Advisory only —
                    a blank or implausible value never blocks saving or publishing. */}
                <div style={{ maxWidth: 260, marginBottom: 12 }}>
                  <FormField label="Fiscal year" hint="Optional. 4-digit calendar year, e.g. 2023.">
                    <input type="number" className="form-input" placeholder="e.g. 2023"
                      value={q.fiscal_year ?? ''}
                      onChange={e => setQ(i, 'fiscal_year', e.target.value === ''
                        ? null
                        : (parseInt(e.target.value, 10) || null))} />
                  </FormField>
                  {!isPlausibleFiscalYear(q.fiscal_year) && (
                    <p className="form-hint" style={{ color: 'var(--color-warning)' }}>
                      That doesn&apos;t look like a calendar year.
                    </p>
                  )}
                </div>
                {showScores && (
                  <>
                    <p className="form-label mb-2">Score Bands</p>
                    {errors[`q_${i}_bands`] && <p className="form-error mb-2">{errors[`q_${i}_bands`]}</p>}
                    {(q.figure_score_bands || []).map((band, bi) => (
                      <div key={bi} className="score-band-row">
                        <FormField label={bi === 0 ? 'Min' : ''}>
                          <input type="number" className="form-input" value={band.min}
                            onChange={e => setBand(i, bi, 'min', e.target.value)} />
                        </FormField>
                        <FormField label={bi === 0 ? 'Max' : ''}>
                          <input type="number" className="form-input" value={band.max}
                            onChange={e => setBand(i, bi, 'max', e.target.value)} />
                        </FormField>
                        <FormField label={bi === 0 ? 'Score' : ''}>
                          <input type="number" step="0.1" className="form-input" value={band.score}
                            onChange={e => setBand(i, bi, 'score', e.target.value)} />
                        </FormField>
                        <div style={{ paddingTop: bi === 0 ? 22 : 0 }}>
                          <Button size="sm" variant="ghost" onClick={() => removeBand(i, bi)}>×</Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="secondary" onClick={() => addBand(i)} className="mt-2">+ Add Score Band</Button>
                  </>
                )}
              </div>
            )}

            {/* Fix 2 Stage A — monetary flag (figure & derived questions) */}
            {(q.response_type === 'figure' || q.response_type === 'derived') && (
              <div className="mt-3" style={{
                padding: '12px 14px', borderRadius: 6,
                background: 'var(--color-border-light)',
                border: '1px solid var(--color-border)',
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!q.is_monetary}
                    onChange={e => {
                      const on = e.target.checked;
                      onChange(questions.map((qItem, idx) => idx === i ? {
                        ...qItem,
                        is_monetary: on,
                        rubric_currency: on ? (qItem.rubric_currency || 'USD') : null,
                      } : qItem));
                    }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Monetary value</span>
                </label>
                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '4px 0 0 24px' }}>
                  Enable if this question asks for a monetary amount (revenue, profit,
                  investment size etc.). This allows currency conversion to ensure fair
                  scoring across different reporting currencies.
                </p>
                {q.is_monetary && (
                  <div style={{ marginTop: 10, marginLeft: 24, maxWidth: 220 }}>
                    <FormField label="Scoring currency"
                      hint="The currency your score bands are defined in. Submitted values will be converted to this currency before scoring.">
                      <select
                        className="form-input"
                        value={q.rubric_currency || 'USD'}
                        onChange={e => setQ(i, 'rubric_currency', e.target.value)}
                      >
                        {MONETARY_CURRENCIES.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                )}
              </div>
            )}

            {/* Date type */}
            {q.response_type === 'date' && (
              <div className="mt-3">
                <SelectField
                  label="Scoring Method"
                  value={q.date_scoring_method || 'binary'}
                  onChange={e => setQ(i, 'date_scoring_method', e.target.value)}
                  options={[
                    { value: 'binary', label: 'Binary — date provided = full score, no date = 0' },
                    { value: 'recency', label: 'Recency scoring — more recent dates score higher' },
                    { value: 'age', label: 'Age scoring — older dates score higher' },
                  ]}
                />
                {(q.date_scoring_method === 'recency' || q.date_scoring_method === 'age') && showScores && (
                  <div className="mt-2">
                    <p className="form-label mb-2">
                      Score Bands{' '}
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                        ({q.date_scoring_method === 'recency' ? 'max years ago → score' : 'min years ago → score'})
                      </span>
                    </p>
                    {(q.date_score_bands || []).map((band, bi) => (
                      <div key={bi} className="score-band-row">
                        <FormField label={bi === 0 ? (q.date_scoring_method === 'recency' ? 'Within N years' : 'At least N years') : ''}>
                          <input
                            type="number" min="0" step="1"
                            className="form-input"
                            placeholder="leave blank = any"
                            value={band.max_years ?? ''}
                            onChange={e => setDateBand(i, bi, 'max_years', e.target.value)}
                          />
                        </FormField>
                        <FormField label={bi === 0 ? 'Score' : ''}>
                          <input
                            type="number" step="0.1" min="0" max="10"
                            className="form-input"
                            value={band.score}
                            onChange={e => setDateBand(i, bi, 'score', e.target.value)}
                          />
                        </FormField>
                        <div style={{ paddingTop: bi === 0 ? 22 : 0 }}>
                          <Button size="sm" variant="ghost" onClick={() => removeDateBand(i, bi)}>×</Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="secondary" onClick={() => addDateBand(i)} className="mt-2">
                      + Add Score Band
                    </Button>
                  </div>
                )}
                {(!q.date_scoring_method || q.date_scoring_method === 'binary') && showScores && (
                  <FormField label="Score when date is provided" className="mt-2" style={{ maxWidth: 160 }}>
                    <input
                      type="number" step="0.1" min="0" max="10"
                      className="form-input"
                      style={{ width: 100 }}
                      value={q.binary_date_score ?? 10}
                      onChange={e => setQ(i, 'binary_date_score', parseFloat(e.target.value) || 0)}
                    />
                  </FormField>
                )}
              </div>
            )}

            {/* Text / Long Text type */}
            {(q.response_type === 'text' || q.response_type === 'long_text') && showScores && (
              <div className="mt-3">
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                  <FormField label="Score when answered" style={{ maxWidth: 160 }}>
                    <input
                      type="number" step="0.1" min="0"
                      className="form-input"
                      style={{ width: 100 }}
                      value={q.text_score ?? 10}
                      onChange={e => setQ(i, 'text_score', parseFloat(e.target.value) || 0)}
                    />
                  </FormField>
                  <FormField label="Score when blank">
                    <input
                      type="number"
                      className="form-input"
                      style={{ width: 100, background: 'var(--color-border-light)', cursor: 'not-allowed' }}
                      value={0}
                      readOnly
                    />
                  </FormField>
                </div>
              </div>
            )}

            {/* Derived type — formula configurator */}
            {q.response_type === 'derived' && (() => {
              // Helper: update a single formula_input reference
              const setInput = (param, questionId) => {
                setQ(i, 'formula_inputs', {
                  ...q.formula_inputs,
                  [param]: { type: 'question', question_id: questionId },
                });
              };
              // Helper: update multi-question list (SUM / AVERAGE)
              const setMultiInput = (param, questionIds) => {
                setQ(i, 'formula_inputs', {
                  ...q.formula_inputs,
                  [param]: questionIds.map(qid => ({ type: 'question', question_id: qid })),
                });
              };
              // Helper (GENERAL): set a coefficient. A blank coefficient is
              // stored by REMOVING the key, so the GENERAL formula applies its
              // own default of 1 rather than resolving an empty constant.
              const setCoefficient = (param, raw) => {
                const next = { ...q.formula_inputs };
                if (raw === '') {
                  delete next[param];
                } else {
                  next[param] = { type: 'constant', value: Number(raw) };
                }
                setQ(i, 'formula_inputs', next);
              };
              // Helper (GENERAL): set a field from the combined picker, which
              // offers every question plus the PIF requested amount.
              // An unselected field is stored by REMOVING the key (the same
              // convention as a blank coefficient). This is load-bearing: an
              // empty { type:'question', question_id:'' } would resolve to a
              // "Missing input" computation_error instead of being treated as
              // an absent optional field.
              const setField = (param, value) => {
                const nextInputs = { ...q.formula_inputs };
                if (value === '') {
                  delete nextInputs[param];
                } else {
                  nextInputs[param] = value === PIF_REQUESTED_AMOUNT
                    ? { type: 'pif_field', field: 'requested_amount' }
                    : { type: 'question', question_id: value };
                }
                // Clearing Field B drops its coefficient and the now-meaningless
                // operator too. formula_inputs and general_config must be written
                // in ONE update: two sequential setQ calls would each read the
                // same stale questions snapshot and the second would clobber the
                // first.
                const clearingFieldB = param === 'field_b' && value === '';
                if (clearingFieldB) {
                  delete nextInputs.coeff_b;
                }
                onChange(questions.map((qItem, idx) => {
                  if (idx !== i) return qItem;
                  const next = { ...qItem, formula_inputs: nextInputs };
                  if (clearingFieldB) {
                    const { operator, ...restConfig } = next.general_config || {};
                    next.general_config = restConfig;
                  }
                  return next;
                }));
              };
              // Helper: update cagr year question at a specific position
              const setCagrYear = (pos, questionId) => {
                const ids = [...(q.cagr_config?.year_question_ids || [])];
                ids[pos] = questionId;
                setQ(i, 'cagr_config', { ...q.cagr_config, year_question_ids: ids });
              };

              const nonDerivedOtherQuestions = nonDerivedQuestions.filter(
                nq => nq.question_id !== q.question_id
              );
              // Fix 1: derived questions may now reference other derived
              // questions as inputs. Include all other questions (derived and
              // non-derived) in the formula-input dropdowns, excluding self.
              // A circular-dependency check in RubricForm blocks cycles.
              const inputQuestions = questions.filter(
                nq => nq.question_id !== q.question_id
              );
              const inputDefs = FORMULA_INPUTS[q.formula_type] || [];
              // GENERAL only: with no Field B the formula is single-term, so the
              // operator and coefficient b are hidden. Every other formula maps
              // over its slots unchanged.
              const visibleDefs =
                q.formula_type === 'GENERAL' && !q.formula_inputs?.field_b
                  ? inputDefs.filter(
                    d => d.param !== 'operator' && d.param !== 'coeff_b'
                  )
                  : inputDefs;
              const preview = buildFormulaPreview(q, nonDerivedOtherQuestions);

              return (
                <div className="mt-3">
                  <div style={{
                    padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 12,
                    background: 'rgba(201,149,42,0.08)', border: '1px solid rgba(201,149,42,0.3)',
                  }}>
                    This question will not appear on the applicant PIF form. Its value is computed
                    automatically at submission time from other question responses.
                  </div>

                  {errors[`q_${i}_derived`] && (
                    <p className="form-error mb-2">{errors[`q_${i}_derived`]}</p>
                  )}

                  {/* Step 1 — Formula selector */}
                  <FormField label="Formula" required className="mb-3">
                    <select
                      className="form-input"
                      value={q.formula_type}
                      onChange={e => {
                        const newType = e.target.value;
                        // Pre-populate pif_field inputs automatically.
                        // Both formula_type and formula_inputs must update in one
                        // onChange call — two sequential setQ calls each read from
                        // the same stale questions snapshot and the second overwrites
                        // the first, leaving formula_type empty.
                        const defs = FORMULA_INPUTS[newType] || [];
                        const prefilledInputs = {};
                        defs.forEach(def => {
                          if (def.type === 'pif_field') {
                            prefilledInputs[def.param] = { type: 'pif_field', field: def.field };
                          }
                        });
                        onChange(questions.map((qItem, idx) => {
                          if (idx !== i) return qItem;
                          return { ...qItem, formula_type: newType, formula_inputs: prefilledInputs };
                        }));
                      }}
                    >
                      <option value="">— Select formula —</option>
                      {FORMULA_GROUPS.map(grp => (
                        <optgroup key={grp.label} label={grp.label}>
                          {grp.formulas.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </FormField>

                  {/* Step 2 — Inputs (the CAGR types have their own panel) */}
                  {isCagrType(q.formula_type) && (
                    <div className="mb-3">
                      <p className="form-label mb-2">CAGR Configuration</p>
                      {q.formula_type === 'CAGR_USD' && (
                        <p className="form-hint mb-2">
                          PAST USD growth. Each historical year is converted to USD at
                          that year&apos;s December year-end rate. The year of each figure
                          question is derived automatically from the submission&apos;s most
                          recent completed financial year (the anchor) — you no longer set
                          a per-question Fiscal year. That year&apos;s rate must exist in the
                          Year-end FX Rates registry; years not yet closed are dropped and
                          a missing rate is reported rather than skipped.
                        </p>
                      )}
                      {q.formula_type === 'CAGR_USD_FUTURE' && (
                        <p className="form-hint mb-2">
                          FORWARD USD growth over PROJECTED revenues. The year questions
                          below must be the company&apos;s <strong>forecast</strong> figures
                          (not the historical revenue slots); slot 1 is the current
                          financial year and each later slot one year on, derived from the
                          submission anchor. Prerequisites before it can compute:
                          {' '}forecast questions authored here, a per-currency
                          <strong> depreciation rate</strong> set in the Year-end FX Rates
                          registry, and a most-recent-completed-financial-year anchor on the
                          submission. Rates are projected from the most recent closed year;
                          fewer than 3 consecutive forecast years scores 0 (not a flat
                          forecast), and a missing depreciation or base rate is reported.
                        </p>
                      )}
                      <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <FormField label="Max years of data">
                          <input type="number" min="2" max="10" className="form-input"
                            value={q.cagr_config?.max_periods ?? 5}
                            onChange={e => setQ(i, 'cagr_config', {
                              ...q.cagr_config,
                              max_periods: parseInt(e.target.value) || 5,
                              year_question_ids: Array(parseInt(e.target.value) || 5).fill(''),
                            })}
                          />
                        </FormField>
                        <FormField label="Min years required">
                          <input type="number" min="2" max={q.cagr_config?.max_periods ?? 5} className="form-input"
                            value={q.cagr_config?.min_periods ?? 2}
                            onChange={e => setQ(i, 'cagr_config', {
                              ...q.cagr_config,
                              min_periods: parseInt(e.target.value) || 2,
                            })}
                          />
                        </FormField>
                      </div>
                      {Array.from({ length: q.cagr_config?.max_periods || 5 }).map((_, yi) => (
                        <FormField key={yi} label={`Year ${yi + 1} ${yi === 0 ? '(oldest)' : yi === (q.cagr_config?.max_periods || 5) - 1 ? '(newest)' : ''}`}>
                          <select className="form-input"
                            value={q.cagr_config?.year_question_ids?.[yi] || ''}
                            onChange={e => setCagrYear(yi, e.target.value)}
                          >
                            <option value="">— Select question —</option>
                            {nonDerivedOtherQuestions
                              .filter(nq => nq.response_type === 'figure')
                              .map(nq => (
                                <option key={nq.question_id} value={nq.question_id}>
                                  {nq.question_text || `(${nq.question_id})`}
                                </option>
                              ))}
                          </select>
                        </FormField>
                      ))}
                    </div>
                  )}

                  {q.formula_type && !isCagrType(q.formula_type) && visibleDefs.map(inp => (
                    <FormField key={inp.param} label={inp.label} className="mb-2">
                      {inp.type === 'pif_field' ? (
                        <input className="form-input" readOnly
                          value="Requested Amount (from PIF form — set automatically)"
                          style={{ background: 'var(--color-border-light)', cursor: 'not-allowed' }}
                          onChange={() => {
                            setQ(i, 'formula_inputs', {
                              ...q.formula_inputs,
                              [inp.param]: { type: 'pif_field', field: inp.field },
                            });
                          }}
                          onFocus={() => {
                            setQ(i, 'formula_inputs', {
                              ...q.formula_inputs,
                              [inp.param]: { type: 'pif_field', field: inp.field },
                            });
                          }}
                        />
                      ) : inp.type === 'multi_question' ? (
                        <select multiple className="form-input" style={{ height: 90 }}
                          value={(q.formula_inputs?.[inp.param] || []).map(r => r.question_id)}
                          onChange={e => setMultiInput(
                            inp.param,
                            Array.from(e.target.selectedOptions, o => o.value)
                          )}
                        >
                          {inputQuestions.map(nq => (
                            <option key={nq.question_id} value={nq.question_id}>
                              {(nq.question_text || `(${nq.question_id})`) +
                                (nq.is_derived ? ' (derived)' : '')}
                            </option>
                          ))}
                        </select>
                      ) : inp.type === 'constant' ? (
                        <input type="number" step="any" className="form-input"
                          placeholder="1"
                          value={q.formula_inputs?.[inp.param]?.value ?? ''}
                          onChange={e => setCoefficient(inp.param, e.target.value)}
                        />
                      ) : inp.type === 'operator' ? (
                        <select className="form-input"
                          value={q.general_config?.operator || ''}
                          onChange={e => setQ(i, 'general_config', {
                            ...q.general_config,
                            operator: e.target.value,
                          })}
                        >
                          <option value="">— Select operator —</option>
                          <option value="+">+ (add)</option>
                          <option value="-">− (subtract)</option>
                          <option value="*">× (multiply)</option>
                          <option value="/">÷ (divide)</option>
                        </select>
                      ) : inp.type === 'field' ? (
                        <select className="form-input"
                          value={
                            q.formula_inputs?.[inp.param]?.type === 'pif_field'
                              ? PIF_REQUESTED_AMOUNT
                              : (q.formula_inputs?.[inp.param]?.question_id || '')
                          }
                          onChange={e => setField(inp.param, e.target.value)}
                        >
                          <option value="">— Select question —</option>
                          {inputQuestions.map(nq => (
                            <option key={nq.question_id} value={nq.question_id}>
                              {(nq.question_text || `(${nq.question_id})`) +
                                (nq.is_derived ? ' (derived)' : '')}
                            </option>
                          ))}
                          <option value={PIF_REQUESTED_AMOUNT}>
                            Requested Amount (from PIF form)
                          </option>
                        </select>
                      ) : (
                        <select className="form-input"
                          value={q.formula_inputs?.[inp.param]?.question_id || ''}
                          onChange={e => setInput(inp.param, e.target.value)}
                        >
                          <option value="">— Select question —</option>
                          {inputQuestions.map(nq => (
                            <option key={nq.question_id} value={nq.question_id}>
                              {(nq.question_text || `(${nq.question_id})`) +
                                (nq.is_derived ? ' (derived)' : '')}
                            </option>
                          ))}
                        </select>
                      )}
                    </FormField>
                  ))}

                  {/* Step 3 — Score bands */}
                  {showScores && (
                    <div className="mb-3">
                      <p className="form-label mb-2">Score Bands</p>
                      {errors[`q_${i}_bands`] && <p className="form-error mb-2">{errors[`q_${i}_bands`]}</p>}
                      {(q.figure_score_bands || []).map((band, bi) => (
                        <div key={bi} className="score-band-row">
                          <FormField label={bi === 0 ? 'Min' : ''}>
                            <input type="number" className="form-input" value={band.min}
                              onChange={e => setBand(i, bi, 'min', e.target.value)} />
                          </FormField>
                          <FormField label={bi === 0 ? 'Max' : ''}>
                            <input type="number" className="form-input" value={band.max}
                              onChange={e => setBand(i, bi, 'max', e.target.value)} />
                          </FormField>
                          <FormField label={bi === 0 ? 'Score' : ''}>
                            <input type="number" step="0.1" className="form-input" value={band.score}
                              onChange={e => setBand(i, bi, 'score', e.target.value)} />
                          </FormField>
                          <div style={{ paddingTop: bi === 0 ? 22 : 0 }}>
                            <Button size="sm" variant="ghost" onClick={() => removeBand(i, bi)}>×</Button>
                          </div>
                        </div>
                      ))}
                      <Button size="sm" variant="secondary" onClick={() => addBand(i)} className="mt-2">+ Add Score Band</Button>
                    </div>
                  )}

                  {/* Step 4 — Display format */}
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <FormField label="Display Format">
                      <select className="form-input"
                        value={q.display_format || 'decimal'}
                        onChange={e => setQ(i, 'display_format', e.target.value)}
                      >
                        <option value="percentage">Percentage (e.g. 24.4%)</option>
                        <option value="decimal">Decimal (e.g. 0.244)</option>
                        <option value="ratio">Ratio (e.g. 1.5x)</option>
                        <option value="integer">Integer (e.g. 1,250)</option>
                        <option value="currency">Currency (e.g. 1,250,000)</option>
                      </select>
                    </FormField>
                    <FormField label="Decimal Places">
                      <input type="number" min="0" max="4" className="form-input"
                        value={q.display_decimal_places ?? 2}
                        onChange={e => setQ(i, 'display_decimal_places', parseInt(e.target.value) || 0)}
                      />
                    </FormField>
                  </div>

                  {/* Step 5 — Preview */}
                  {preview && (
                    <div style={{
                      padding: '10px 14px', borderRadius: 6, fontSize: 13,
                      background: 'var(--color-border-light)', border: '1px solid var(--color-border)',
                    }}>
                      <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                        color: 'var(--color-text-secondary)', marginBottom: 4 }}>Formula preview</p>
                      <p>{preview}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      ))}
      <Button variant="secondary" onClick={add} className="mt-2">+ Add Question</Button>
    </div>
  );
}
